import React, { useState, useEffect, useRef } from 'react';
import { Plus } from 'lucide-react';
import { API_URL, adminFetch } from './shared';
import { InventoryProductCard } from './InventoryProductCard';
import { AddProductModal } from './AddProductModal';
import { EditProductModal } from './EditProductModal';
import { FilterGroupsModal } from './FilterGroupsModal';
import { AnnouncementModal } from './AnnouncementModal';

export const InventoryTab = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [saving, setSaving] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [showGroupsModal, setShowGroupsModal] = useState(false);
  const [showAnnModal, setShowAnnModal] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const dragFrom = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const fetchProducts = async () => {
    setLoading(true);
    setFetchError(false);
    // Single fetch with 10s timeout. If it fails, show error immediately instead
    // of waiting ~90s through exponential backoff retries (bad UX).
    try {
      const ctrl = new AbortController();
      const timeoutId = setTimeout(() => ctrl.abort(), 10000);
      const res = await fetch(`${API_URL}/api/products?_t=${Date.now()}`, {
        cache: 'no-store',
        signal: ctrl.signal,
      });
      clearTimeout(timeoutId);
      if (res.ok) {
        setProducts(await res.json());
        setLoading(false);
        return;
      }
      console.error('Inventory fetch returned status:', res.status);
    } catch (err) {
      console.error('Inventory fetch failed:', err);
    }
    setLoading(false);
    setFetchError(true);
  };

  useEffect(() => { fetchProducts(); }, []);

  const patchProduct = async (productId, body, optimisticUpdate) => {
    setSaving(productId);
    try {
      const res = await adminFetch(`/api/products/${productId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        setProducts(prev => prev.map(p => p.product_id === productId ? { ...p, ...optimisticUpdate } : p));
      } else {
        alert('Error al guardar. Intenta de nuevo.');
      }
    } catch (err) {
      console.error('Patch failed:', err);
      alert('No se pudo conectar al servidor. Verifica tu conexión e intenta de nuevo.');
    }
    finally { setSaving(null); }
  };

  const applyOrder = (list) => {
    const renumbered = list.map((p, i) => ({ ...p, sort_order: i + 1 }));
    setProducts(renumbered);
    renumbered.forEach((p, i) => {
      const prev = products.find(x => x.product_id === p.product_id);
      if (prev?.sort_order !== i + 1) {
        adminFetch(`/api/products/${p.product_id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sort_order: i + 1 }),
        }).catch(() => {});
      }
    });
  };

  const moveProduct = (productId, dir) => {
    const idx = products.findIndex(p => p.product_id === productId);
    const to = idx + dir;
    if (idx === -1 || to < 0 || to >= products.length) return;
    const list = [...products];
    [list[idx], list[to]] = [list[to], list[idx]];
    applyOrder(list);
  };

  const handleDrop = (toIdx) => {
    const fromIdx = dragFrom.current;
    dragFrom.current = null;
    setDragOverIdx(null);
    if (fromIdx == null || toIdx == null || fromIdx === toIdx) return;
    const list = [...products];
    const [moved] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, moved);
    applyOrder(list);
  };

  const handleTouchDragMove = (x, y) => {
    const el = document.elementFromPoint(x, y);
    const wrap = el?.closest('[data-drag-idx]');
    if (wrap) setDragOverIdx(parseInt(wrap.dataset.dragIdx, 10));
  };

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const duplicateProduct = async (id) => {
    setSaving(id);
    try {
      const res = await adminFetch(`/api/admin/products/${id}/duplicate`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        await fetchProducts();
        window.dispatchEvent(new Event('productsUpdated'));
        alert(`Producto duplicado como #${data.product_id} (oculto, con stock en 0). Edítalo para personalizarlo.`);
      } else {
        alert('No se pudo duplicar el producto');
      }
    } catch (e) { alert('Error al duplicar'); }
    finally { setSaving(null); }
  };

  const bulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`¿Eliminar ${selected.length} producto(s) permanentemente? Esta acción no se puede deshacer.`)) return;
    setBulkDeleting(true);
    const failed = [];
    for (const id of selected) {
      try {
        const res = await adminFetch(`/api/admin/products/${id}`, { method: 'DELETE' });
        if (!res.ok) failed.push(id);
      } catch (e) { failed.push(id); }
    }
    setBulkDeleting(false);
    setSelected([]);
    setSelectMode(false);
    await fetchProducts();
    window.dispatchEvent(new Event('productsUpdated'));
    if (failed.length) alert(`No se pudieron eliminar: ${failed.join(', ')}`);
  };

  if (loading) return (
    <div className="text-center py-12 text-slate-500">
      <div className="inline-block w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mb-3"></div>
      <p>Cargando inventario...</p>
    </div>
  );

  if (fetchError) return (
    <div className="text-center py-12">
      <p className="text-slate-500 mb-4">No se pudieron cargar los productos. El servidor puede estar iniciando.</p>
      <button onClick={fetchProducts} className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors" data-testid="retry-inventory">
        Reintentar
      </button>
    </div>
  );

  // Global stock summary (iterate product sizes so missing keys count as 0)
  let totalUnits = 0, lowSizes = 0, oosSizes = 0;
  products.forEach(p => {
    const sq = p.stock_qty || {};
    if (Object.keys(sq).length === 0) return; // untracked product
    const th = p.low_stock_threshold ?? 3;
    (p.sizes?.es || Object.keys(sq)).forEach(size => {
      const n = parseInt(sq[size], 10) || 0;
      totalUnits += n;
      if (n <= 0) oosSizes += 1;
      else if (n <= th) lowSizes += 1;
    });
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-center">
            <p className="text-lg font-black text-slate-800 leading-none" data-testid="stock-total-units">{totalUnits}</p>
            <p className="text-[9px] text-slate-500 uppercase font-semibold">Unidades</p>
          </div>
          <div className="bg-white border border-amber-200 rounded-lg px-3 py-1.5 text-center">
            <p className="text-lg font-black text-amber-600 leading-none">{lowSizes}</p>
            <p className="text-[9px] text-slate-500 uppercase font-semibold">Tallas bajas</p>
          </div>
          <div className="bg-white border border-red-200 rounded-lg px-3 py-1.5 text-center">
            <p className="text-lg font-black text-red-600 leading-none">{oosSizes}</p>
            <p className="text-[9px] text-slate-500 uppercase font-semibold">Agotadas</p>
          </div>
          <p className="text-xs text-slate-500 ml-1">{products.length} productos</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {selectMode ? (
            <>
              <span className="text-xs font-semibold text-slate-600">{selected.length} seleccionado(s)</span>
              <button
                onClick={bulkDelete}
                disabled={selected.length === 0 || bulkDeleting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 transition-colors shadow-sm disabled:opacity-50"
                data-testid="bulk-delete-btn"
              >
                {bulkDeleting ? 'Eliminando…' : `Eliminar (${selected.length})`}
              </button>
              <button
                onClick={() => { setSelectMode(false); setSelected([]); }}
                className="px-3 py-1.5 bg-white border border-slate-200 text-slate-600 text-xs font-semibold rounded-lg hover:bg-slate-50 transition-colors"
                data-testid="cancel-select-btn"
              >
                Cancelar
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setSelectMode(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 text-slate-700 text-xs font-semibold rounded-lg hover:bg-slate-50 transition-colors shadow-sm"
                data-testid="select-mode-btn"
              >
                ☑ Seleccionar
              </button>
              <button
                onClick={() => setShowAnnModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 text-slate-700 text-xs font-semibold rounded-lg hover:bg-slate-50 transition-colors shadow-sm"
                data-testid="open-announcement-modal"
              >
                📣 Anuncio
              </button>
              <button
                onClick={() => setShowGroupsModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 text-slate-700 text-xs font-semibold rounded-lg hover:bg-slate-50 transition-colors shadow-sm"
                data-testid="open-filter-groups-modal"
              >
                Filtros del catálogo
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-cyan-600 text-white text-xs font-semibold rounded-lg hover:bg-cyan-700 transition-colors shadow-sm"
                data-testid="open-add-product-modal"
              >
                <Plus className="w-4 h-4" />
                Añadir producto
              </button>
            </>
          )}
        </div>
      </div>
      <div className="space-y-1.5">
        {products.map((product, pi) => (
          <div
            key={product.product_id}
            data-drag-idx={pi}
            onDragOver={(e) => { e.preventDefault(); if (dragFrom.current != null) setDragOverIdx(pi); }}
            onDrop={(e) => { e.preventDefault(); handleDrop(pi); }}
            className={dragOverIdx === pi && dragFrom.current !== pi ? 'ring-2 ring-cyan-400 rounded-xl' : ''}
          >
            <InventoryProductCard
              product={product}
              saving={saving}
              onPatch={patchProduct}
              onEdit={(p) => setEditProduct(p)}
              onMove={moveProduct}
              isFirst={pi === 0}
              isLast={pi === products.length - 1}
              selectMode={selectMode}
              isSelected={selected.includes(product.product_id)}
              onToggleSelect={toggleSelect}
              onDuplicate={duplicateProduct}
              onDragStartCard={() => { dragFrom.current = pi; }}
              onDragEndCard={() => { dragFrom.current = null; setDragOverIdx(null); }}
              onTouchDragStart={() => { dragFrom.current = pi; }}
              onTouchDragMove={handleTouchDragMove}
              onTouchDrop={() => handleDrop(dragOverIdx)}
              onDelete={async (id) => {
                if (!window.confirm(`¿Eliminar producto #${id} permanentemente? Esta acción no se puede deshacer.`)) return;
                try {
                  const res = await adminFetch(`/api/admin/products/${id}`, { method: 'DELETE' });
                  if (res.ok) {
                    setProducts(prev => prev.filter(p => p.product_id !== id));
                  } else {
                    alert('No se pudo eliminar el producto');
                  }
                } catch (e) {
                  alert('Error al eliminar');
                }
              }}
            />
          </div>
        ))}
      </div>
      {showAnnModal && (
        <AnnouncementModal onClose={() => setShowAnnModal(false)} />
      )}
      {showGroupsModal && (
        <FilterGroupsModal
          products={products}
          onClose={() => setShowGroupsModal(false)}
        />
      )}
      {showAddModal && (
        <AddProductModal
          onClose={() => setShowAddModal(false)}
          onCreated={() => { setShowAddModal(false); fetchProducts(); window.dispatchEvent(new Event('productsUpdated')); }}
        />
      )}
      {editProduct && (
        <EditProductModal
          product={editProduct}
          onClose={() => setEditProduct(null)}
          onSaved={() => { setEditProduct(null); fetchProducts(); window.dispatchEvent(new Event('productsUpdated')); }}
        />
      )}
    </div>
  );
};
